/**
 * Wizard card for the verify_ads_active step (the Verify-group closer for
 * attribution).
 *
 * "Active" has no single Ads API field — the real signal is whether each
 * conversion action has RECORDED conversions (status ENABLED + all_conversions
 * > 0 in the lookback window), which matches the Ads UI's "Recording
 * conversions" state. Conversions lag hours after the tag fires, so this is a
 * manual Refresh (no cron, mirroring DomainCard's verify-DNS): the operator
 * runs the smoke test / drives traffic, then refreshes until the primary
 * actions go green.
 *
 * No secret here — Ads credentials are platform env; the customer ID is read
 * server-side from settings.
 */
import { CheckCircle2, Circle, Loader2, RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { OnboardingTask } from '@limo/database'
import { useInvokeOnboardingStep } from '@/hooks/useOnboarding'
import { useToast } from '@/hooks/use-toast'
import { StepCard } from './StepCard'
import { GoogleAdsAccountBadge } from './GoogleAdsAccountBadge'

interface VerifyAdsActiveCardProps {
  task: OnboardingTask
  accountId: string
  runId: string
}

interface ActivityResult {
  name: string
  status: string
  conversions: number
  recording: boolean
}

interface VerifyAdsActiveResult {
  actions?: ActivityResult[]
  checkedAt?: string
}

export function VerifyAdsActiveCard({
  task,
  accountId,
  runId,
}: VerifyAdsActiveCardProps) {
  const { toast } = useToast()
  const invokeStep = useInvokeOnboardingStep(runId)

  const result = (task.result ?? {}) as VerifyAdsActiveResult
  const actions = result.actions ?? []
  const isDone = task.status === 'done'

  const verifyActive = () =>
    invokeStep.mutate(
      {
        functionName: 'onboard-google-ads',
        accountId,
        params: { action: 'verify_active' },
      },
      {
        onError: (error) =>
          toast({
            title: 'Ads activity check failed',
            description: error.message,
            variant: 'destructive',
          }),
      }
    )

  return (
    <StepCard
      task={task}
      manualFallback={
        <div className="space-y-2">
          <p>
            A conversion action only shows &ldquo;Recording conversions&rdquo;
            after the tag fires <em>and</em> Google ingests it — which can take
            hours. Run the smoke test (or wait for live traffic), then Refresh.
          </p>
          <p>
            Manual check: Google Ads &gt; Goals &gt; Conversions — the Status
            column should read &ldquo;Recording conversions&rdquo; for Purchase
            and Get Vehicle Quotes.
          </p>
          <p>
            Prerequisites: <code>ads_conversions</code> + <code>conversion_labels</code>{' '}
            done, and the GTM/Stape tagging live on the domain.
          </p>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <GoogleAdsAccountBadge accountId={accountId} />
        {actions.length > 0 ? (
          <>
            <div className="overflow-hidden rounded-md border">
              <table className="w-full text-xs">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-2 py-1 text-left font-medium">Action</th>
                    <th className="px-2 py-1 text-left font-medium">Status</th>
                    <th className="px-2 py-1 text-right font-medium">Conv.</th>
                    <th className="px-2 py-1 text-left font-medium">Recording</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {actions.map((a) => (
                    <tr key={a.name}>
                      <td className="px-2 py-1 font-mono">{a.name}</td>
                      <td className="px-2 py-1">{a.status}</td>
                      <td className="px-2 py-1 text-right tabular-nums">
                        {a.conversions}
                      </td>
                      <td className="px-2 py-1">
                        {a.recording ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant={isDone ? 'outline' : 'default'}
                onClick={verifyActive}
                disabled={invokeStep.isPending}
              >
                {invokeStep.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Refresh
              </Button>
              {isDone && (
                <span className="flex items-center gap-1 text-xs text-green-700">
                  <CheckCircle2 className="h-4 w-4" /> Conversions recording
                </span>
              )}
            </div>
            {!isDone && (
              <p className="text-xs text-muted-foreground">
                Conversions can take hours to appear after the tag fires — Refresh
                later to re-check.
              </p>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <p className="text-muted-foreground">
              Checks whether the conversion actions are recording conversions.
              Run the smoke test first, then check here.
            </p>
            <Button
              type="button"
              size="sm"
              onClick={verifyActive}
              disabled={invokeStep.isPending}
            >
              {invokeStep.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-2 h-4 w-4" />
              )}
              Check now
            </Button>
          </div>
        )}
      </div>
    </StepCard>
  )
}
